// src/services/statsService.js
// Servicio para registrar visitas y manejar estadísticas
import { getBackendUrl } from '../config/config';
import { getSafeToken } from '../utils/tokenHelper';

class StatsService {
  constructor() {
    this.visitedPages = new Set();
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Detectar tipo de dispositivo
  getDeviceType() {
    const userAgent = navigator.userAgent;
    if (/Mobile|Android|iPhone|Windows Phone/.test(userAgent)) {
      if (/iPad|Tablet/.test(userAgent)) {
        return 'tablet';
      }
      return 'mobile';
    }
    return 'desktop';
  }

  // Obtener país aproximado (simplificado)
  getApproximateCountry() {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const countryMap = {
      'America/Lima': 'Perú',
      'America/Bogota': 'Colombia',
      'America/Mexico_City': 'México',
      'America/Argentina/Buenos_Aires': 'Argentina',
      'Europe/Madrid': 'España',
      'America/Santiago': 'Chile',
      'America/Guayaquil': 'Ecuador',
      'America/Sao_Paulo': 'Brasil',
      'America/New_York': 'Estados Unidos',
      'America/Caracas': 'Venezuela'
    };

    return countryMap[timezone] || 'Perú';
  }

  // Registrar visita a una página
  async recordPageVisit(page = window.location.pathname) {
    try {
      // Verificar si es una visita única a esta página en esta sesión
      const isUniqueVisitor = !this.visitedPages.has(page);
      
      if (isUniqueVisitor) {
        this.visitedPages.add(page);
      }

      const visitData = {
        page,
        country: this.getApproximateCountry(),
        isUniqueVisitor,
        deviceType: this.getDeviceType(),
        sessionId: this.sessionId,
        timestamp: new Date().toISOString()
      };

      // Registro de visitas REACTIVADO
      console.log('📊 Registrando visita real en backend...');
      console.log('📊 Datos a enviar:', visitData);
      
      const response = await fetch(`${getBackendUrl()}/api/stats/record-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(visitData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('📊 Visita registrada exitosamente:', {
          page, totalVisits: result.totalVisits, todayVisits: result.todayVisits
        });
        return result;
      } else {
        console.warn('⚠️ Error al registrar visita:', response.statusText);
      }

    } catch (error) {
      console.warn('⚠️ Error al registrar visita:', error);
    }
  }

  // Obtener estadísticas (temporalmente deshabilitado)
  async getStats() {
    try {
      console.log('📊 getStats() - Obteniendo datos reales del backend');
      
      const token = getSafeToken();
      if (!token) throw new Error('Token de autenticación requerido');

      const response = await fetch(`${getBackendUrl()}/api/stats/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📊 Datos reales recibidos desde statsService:', data);
        return data;
      } else if (response.status === 403) {
        throw new Error('Acceso denegado. Solo administradores.');
      } else {
        throw new Error('Error al obtener estadísticas');
      }

    } catch (error) {
      console.error('Error getting stats:', error);
      throw error;
    }
  }

  // Enviar reporte por email (requiere autenticación de admin)
  async sendReport(reportType = 'monthly', email = 'informes@edutech.pe') {
    try {
      const token = getSafeToken();
      
      if (!token) {
        throw new Error('Token de autenticación requerido');
      }

      const response = await fetch(`${getBackendUrl()}/api/stats/send-report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reportType, email })
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else if (response.status === 403) {
        throw new Error('Acceso denegado. Solo administradores.');
      } else {
        throw new Error('Error al enviar reporte');
      }

    } catch (error) {
      console.error('Error sending report:', error);
      throw error;
    }
  }

  // Resetear estadísticas (requiere autenticación de admin)
  async resetStats(resetType = 'all') {
    try {
      const token = getSafeToken();
      
      if (!token) {
        throw new Error('Token de autenticación requerido');
      }

      const response = await fetch(`${getBackendUrl()}/api/stats/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ resetType })
      });

      if (response.ok) {
        const result = await response.json();
        return result;
      } else if (response.status === 403) {
        throw new Error('Acceso denegado. Solo administradores.');
      } else {
        throw new Error('Error al resetear estadísticas');
      }

    } catch (error) {
      console.error('Error resetting stats:', error);
      throw error;
    }
  }
}

// Exportar instancia singleton
export default new StatsService();